import { apiClient } from '@isocel/front-common';
import { useMutation } from 'react-query';

import {
    CreateInvoicePayload,
    CreateInvoiceResponse,
    GetInvoicesParams,
    Invoice,
    InvoiceDetailsResponse,
    InvoicePreviewResponse,
} from './types';

export interface PreviewInvoiceParams {
    organizationId: string;
    startDate: string; // ISO string (yyyy-mm-ddT00:00:00)
    endDate: string; // ISO string (yyyy-mm-ddT23:59:59)
    storeIds: string[];
}

/**
 * Runs the manual invoicing algorithm without persisting anything.
 * Returns the "Quantité à facturer" per store × package plus the ids of the
 * packagingFlows that were used (needed by the create endpoint to link them
 * to the freshly created invoice).
 */
export const previewInvoice = async (params: PreviewInvoiceParams): Promise<InvoicePreviewResponse> => {
    const { data } = await apiClient().get('/invoices/preview', {
        params: {
            organizationId: params.organizationId,
            startDate: params.startDate,
            endDate: params.endDate,
            storeIds: params.storeIds.join(','),
        },
    });
    return data;
};

/**
 * Creates the invoice + invoiceDetails documents and stamps every matching
 * packagingFlow with the new invoiceId.
 */
export const createInvoice = async (payload: CreateInvoicePayload): Promise<CreateInvoiceResponse> => {
    const { data } = await apiClient().post('/invoices', payload);
    return data;
};

export const useCreateInvoice = () => useMutation(createInvoice);

/**
 * Lists invoices for the "Historique de facturation" screen.
 * `startDate`/`endDate` filter on invoice.createdAt (date d'émission) and are both optional.
 */
export const getInvoices = async (params: GetInvoicesParams): Promise<Invoice[]> => {
    const { data } = await apiClient().get('/invoices', {
        params: {
            organizationId: params.organizationId,
            storeIds: params.storeIds && params.storeIds.length > 0 ? params.storeIds.join(',') : undefined,
            startDate: params.startDate || undefined,
            endDate: params.endDate || undefined,
        },
    });
    return data;
};

/**
 * Fetches an invoice and all its invoiceDetails, already enriched with the
 * store / package info required by the details drawer.
 */
export const getInvoiceDetails = async (invoiceId: string): Promise<InvoiceDetailsResponse> => {
    const { data } = await apiClient().get(`/invoices/${invoiceId}/details`);
    return data;
};
